import { action, observable, makeObservable, runInAction } from "mobx";
import ShipmentsAPI from "../api/ShipmentsAPI";
import JobAPI from "../api/JobAPI";
import { JobRecord, JobTypes } from "../../../models/Jobs/JobRecord";
import CarriersAPI from "../api/CarriersAPI";
import { Carrier } from "../../../models/Carriers/Carrier";
import {
  BulkUpdate,
  BulkUpdateShippingCostResult,
  DeliveryPartner,
  ShipmentBookingType,
  ShipmentMethod,
  Shipments,
  ShipmentsDetails,
  TabWiseShipmentsCount,
  TrackingBulkUpdateJobStatus,
  TrackingBulkUpdateResult,
} from "../../../models/Shipments";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  getShipmentSectionApiErrorMessage,
  ShipmentDetailsApiRow,
  UpdateShipmentAddressRequest,
  UpdateShipmentAddressResult,
  UpdateShipmentDeliveryRequest,
  UpdateShipmentDeliveryResult,
  UpdateShipmentRecipientRequest,
  UpdateShipmentRecipientResult,
} from "../../../models/Shipments/ShipmentDetails";
import {
  AddShipmentBoxRequest,
  AddShipmentBoxResult,
  BulkAddShipmentBoxResult,
  DeleteShipmentBoxResult,
  getShipmentBoxApiErrorMessage,
} from "../../../models/Shipments/ShipmentBoxes";
import {
  AddShipmentItemLineRequest,
  AddShipmentItemLineResult,
  AddShipmentPackLineRequest,
  AddShipmentPackLineResult,
  BulkAddShipmentItemLineRequest,
  BulkAddShipmentPackLineRequest,
  RemoveShipmentLineResult,
  RemoveShipmentPackLinesResult,
  ShipmentLineType,
  ShipmentLinesUploadJobStatus,
  ShipmentLinesUploadResult,
  ShipmentLinesUploadError,
  getShipmentLinesUploadErrorMessage,
  getShipmentLinesUploadRunningJobId,
} from "../../../models/Shipments/ShipmentInventoryLines";
import {
  ShipmentCreateOptions,
  ShipmentRequest,
} from "../../../models/AddShipment";
import { UserStore } from "../../../stores/UserStore";
import { APIResponse } from "../../../models/NetworkResponse";
import {
  BulkBookingSyncResult,
  SingleBookingSyncResult,
  getBookingSyncErrorMessage,
} from "../../../models/Shipments/ShipmentBooking";

const ShipmentStatus = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out for delivery" },
  { id: 5, value: "Delivered" },
  { id: 6, value: "Return Requested" },
  { id: 7, value: "Returned" },
  { id: 8, value: "Cancelled" },
];
export interface GetShipmentsParams {
  page?: number;
  limit?: number;
  tab_id?: string;
  sortBy?: string;
  allSelected?: boolean;
  sortType?: "ASC" | "DESC";
  search?: string;
  shipment_type?: number[];
  status?: number[];
  delivery_partner_id?: number[];
  order_id?: number[];
  dimensions?: number[];
  emails?: string;
  c_id?: FilterValue[];
  inventory_type?: string;
  clientSku?: FilterValue[];
  packs_exist?: boolean | undefined;
  country?: string[] | undefined;
  search_text_value?: (number | string)[] | undefined;
  contact_number?: string | undefined;
}

export interface GetPacksParams {
  shipment_ids: number[];
  inventory_id: number;
  available_only?: boolean;
  consumed_only?: boolean;
  search_text?: string;
}

const toDeliveryPartner = (carrier: Carrier): DeliveryPartner => ({
  active: carrier.is_active ? 1 : 0,
  created_by: carrier.created_by ?? 0,
  created_date: carrier.created_at,
  modified_date: carrier.updated_at,
  delivery_partner_id: carrier.id,
  delivery_partner_name: carrier.name,
});

const LINES_UPLOAD_JOB_STALE_MS = 30 * 60 * 1000;

export class ShipmentStore {
  shipmentsAPI: ShipmentsAPI;
  carriersAPI: CarriersAPI;
  shipments?: Shipments[] = [];
  totalCount: number = 0;
  totalPages: number = 0;
  deliveryPartners: DeliveryPartner[] = [];
  loading: boolean = false;
  error: string | null = null;
  orderIds: any[];
  c_id: any[] = [];
  searchedPacks: FilterValue[];
  countByStatus: TabWiseShipmentsCount[] = [];
  bulkShippingCostDraft = "";
  jobAPI: JobAPI;
  runningLinesUploadJobs: JobRecord[] = [];
  runningLinesUploadJobsLoading = false;
  runningLinesUploadJobsError: string | null = null;

  private deliveryPartnerRequestId = 0;

  constructor() {
    this.shipmentsAPI = new ShipmentsAPI();
    this.carriersAPI = new CarriersAPI();
    this.jobAPI = new JobAPI();
    this.orderIds = [];
    this.searchedPacks = [];

    makeObservable(this, {
      shipments: observable,
      totalCount: observable,
      totalPages: observable,
      getShipments: action,
      editShipment: action,
      updateShipmentRecipient: action,
      updateShipmentAddress: action,
      updateShipmentDelivery: action,
      patchListingRow: action,
      cancelShipment: action,
      returnShipment: action,
      deliveryPartners: observable,
      loading: observable,
      error: observable,
      reset: action,
      listDeliveryPartners: action,
      listPrinterVendors: action,
      orderIds: observable,
      getOrder: action,
      searchedPacks: observable,
      searchPacksByName: action,
      createShipmentThroughCsv: action,
      createBulkShipmentsViaInventoryIdAndCsv: action,
      countByStatus: observable,
      getCountByStatus: action,
      moveShipmentToNew: action,
      syncBooking: action,
      bulkUpdateShippingCost: action,
      bulkShippingCostDraft: observable,
      setBulkShippingCostDraft: action,
      clearBulkShippingCostDraft: action,
      syncShipmentBooking: action,
      runningLinesUploadJobs: observable,
      runningLinesUploadJobsLoading: observable,
      runningLinesUploadJobsError: observable,
    });
  }

  reset = () => {
    this.shipments = [];
    this.totalCount = 0;
    this.totalPages = 0;
    this.deliveryPartners = [];
    this.loading = false;
    this.error = null;
    this.countByStatus = [];
    this.orderIds = [];
    this.searchedPacks = [];
    this.bulkShippingCostDraft = "";
  };

  setBulkShippingCostDraft = (value: string) => {
    this.bulkShippingCostDraft = value;
  };

  clearBulkShippingCostDraft = () => {
    this.bulkShippingCostDraft = "";
  };

  setLoadingAndError = action(
    (loading: boolean, errorMessage: string | null) => {
      this.loading = loading;
      this.error = errorMessage;
    },
  );

  getShipments = async (
    params: GetShipmentsParams = {},
    download_csv: boolean = false,
    signal?: AbortSignal,
  ) => {
    try {
      if (!download_csv) {
        this.setLoadingAndError(true, null);
      }

      const response = await this.shipmentsAPI.getShipments(
        params,
        download_csv,
        signal,
      );
      if (!download_csv) {
        this.shipments = response.list;
        this.totalCount = response.total_count;
        this.totalPages = response.total_pages;
      }
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      // Ignore abort - request was cancelled on tab/filter change
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
        this.setLoadingAndError(false, null);
        return;
      }
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  downloadBulkShipmentsCSVReport = async (params: GetShipmentsParams = {}) => {
    try {
      const response =
        await this.shipmentsAPI.downloadBulkShipmentsCSVReport(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  listDeliveryPartners = async (searchText?: string) => {
    const requestId = ++this.deliveryPartnerRequestId;
    try {
      const search = searchText?.trim();
      const response = await this.carriersAPI.listCarriers({
        search: search ? search : undefined,
        is_active: 1,
        page_no: 1,
        limit: 200,
      });
      if (requestId !== this.deliveryPartnerRequestId) {
        return;
      }
      this.deliveryPartners = response.list.map(toDeliveryPartner);
    } catch (error: any) {
      if (requestId !== this.deliveryPartnerRequestId) {
        return;
      }
      this.setLoadingAndError(
        false,
        error?.response?.data?.message || error?.message,
      );
    }
  };

  listPrinterVendors = async (searchText?: string) => {
    const requestId = ++this.deliveryPartnerRequestId;
    try {
      const printers = await this.shipmentsAPI.listPrinterVendors(searchText);
      if (requestId !== this.deliveryPartnerRequestId) {
        return;
      }
      this.deliveryPartners = printers;
    } catch (error: any) {
      if (requestId !== this.deliveryPartnerRequestId) {
        return;
      }
      this.setLoadingAndError(
        false,
        error?.response?.data?.message || error?.message,
      );
    }
  };

  editShipment = async (
    shipmentId: number,
    shipment: ShipmentsDetails,
    userStore?: UserStore,
  ) => {
    try {
      // Do not toggle listing `loading` here. UniversalShipments unmounts
      // ShipmentsTable while loading, which would close the edit popup.
      this.setLoadingAndError(this.loading, null);
      const response = await this.shipmentsAPI.editShipment(
        shipmentId,
        shipment,
      );

      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          const statusValue = shipment.status
            ? ShipmentStatus.find((s) => s.id === shipment.status)?.value
            : undefined;

          const deliveryPartnerValue = shipment.delivery_partner_id
            ? this.deliveryPartners.find(
                (dp) => dp.delivery_partner_id === shipment.delivery_partner_id,
              )?.delivery_partner_name || shipment.delivery_partner_name
            : undefined;

          const modifiedDate = new Date().toISOString();
          const modifiedById = userStore?.user?.id;
          const modifiedByName = userStore?.user?.name;

          this.shipments[index] = {
            ...this.shipments[index],
            ...shipment,
            ...(shipment.status !== undefined && {
              status: shipment.status,
              status_label: statusValue,
            }),
            ...(shipment.delivery_partner_id !== undefined && {
              delivery_partner_id: shipment.delivery_partner_id,
              delivery_partner_name: deliveryPartnerValue,
            }),
            ...(shipment.shipment_method !== undefined && {
              shipment_method: shipment.shipment_method,
            }),
            ...(shipment.booking_type !== undefined && {
              booking_type: shipment.booking_type,
            }),
            modified_date: modifiedDate,
            ...(modifiedById !== undefined && { modified_by_id: modifiedById }),
            ...(modifiedByName !== undefined && {
              modified_by_name: modifiedByName,
            }),
          };
        }
      }

      this.setLoadingAndError(this.loading, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(
        this.loading,
        error?.response?.data?.message ||
          error?.message ||
          "Error saving shipment",
      );
      throw error;
    }
  };

  patchListingRow = (
    shipmentId: number,
    patch: Partial<Shipments>,
    userStore?: UserStore,
  ) => {
    try {
      if (!this.shipments) {
        return;
      }

      const index = this.shipments.findIndex((item) => item.id === shipmentId);
      if (index === -1) {
        return;
      }

      const modifiedDate = new Date().toISOString();
      const modifiedById = userStore?.user?.id;
      const modifiedByName = userStore?.user?.name;

      this.shipments[index] = {
        ...this.shipments[index],
        ...patch,
        modified_date: modifiedDate,
        ...(modifiedById !== undefined && { modified_by_id: modifiedById }),
        ...(modifiedByName !== undefined && {
          modified_by_name: modifiedByName,
        }),
      };
    } catch {
      // Listing stays as last successful payload if a patch fails.
    }
  };

  updateShipmentRecipient = async (
    shipmentId: number,
    payload: UpdateShipmentRecipientRequest,
    userStore?: UserStore,
  ): Promise<UpdateShipmentRecipientResult> => {
    try {
      this.setLoadingAndError(this.loading, null);
      const result = await this.shipmentsAPI.updateShipmentRecipient(
        shipmentId,
        payload,
      );
      this.patchListingRow(
        shipmentId,
        {
          first_name: result.first_name,
          last_name: result.last_name ?? "",
          email: result.email,
          phone: result.phone,
          alternate_phone: result.alternate_phone ?? "",
        },
        userStore,
      );
      this.setLoadingAndError(this.loading, null);
      return result;
    } catch (error: unknown) {
      this.setLoadingAndError(
        this.loading,
        getShipmentSectionApiErrorMessage(error, "Failed to update recipient."),
      );
      throw error;
    }
  };

  updateShipmentAddress = async (
    shipmentId: number,
    payload: UpdateShipmentAddressRequest,
    userStore?: UserStore,
  ): Promise<UpdateShipmentAddressResult> => {
    try {
      this.setLoadingAndError(this.loading, null);
      const result = await this.shipmentsAPI.updateShipmentAddress(
        shipmentId,
        payload,
      );
      this.patchListingRow(
        shipmentId,
        {
          address1: result.address1,
          address2: result.address2 ?? "",
          city: result.city,
          state: result.state,
          country: result.country,
          pincode: result.pincode,
          order_notes: result.order_notes ?? "",
        },
        userStore,
      );
      this.setLoadingAndError(this.loading, null);
      return result;
    } catch (error: unknown) {
      this.setLoadingAndError(
        this.loading,
        getShipmentSectionApiErrorMessage(
          error,
          "Failed to update shipping address.",
        ),
      );
      throw error;
    }
  };

  updateShipmentDelivery = async (
    shipmentId: number,
    payload: UpdateShipmentDeliveryRequest,
    userStore?: UserStore,
  ): Promise<UpdateShipmentDeliveryResult> => {
    try {
      this.setLoadingAndError(this.loading, null);
      const result = await this.shipmentsAPI.updateShipmentDelivery(
        shipmentId,
        payload,
      );

      const statusValue = ShipmentStatus.find(
        (status) => status.id === result.status,
      )?.value;
      const deliveryPartnerValue =
        result.delivery_partner_id != null
          ? this.deliveryPartners.find(
              (dp) => dp.delivery_partner_id === result.delivery_partner_id,
            )?.delivery_partner_name
          : undefined;

      this.patchListingRow(
        shipmentId,
        {
          expected_delivery_date: result.expected_delivery_date ?? "",
          dispatch_date: result.dispatch_date ?? "",
          delivery_partner_id: result.delivery_partner_id ?? undefined,
          ...(deliveryPartnerValue !== undefined && {
            delivery_partner_name: deliveryPartnerValue,
          }),
          ...(result.delivery_partner_id == null && {
            delivery_partner_name: "",
          }),
          shipment_method: result.shipment_method
            ? (String(result.shipment_method).toUpperCase() as ShipmentMethod)
            : undefined,
          booking_type: result.booking_type ?? null,
          status: result.status,
          ...(statusValue !== undefined && { status_label: statusValue }),
          shipping_cost: result.shipping_cost ?? undefined,
          awb: result.awb ?? "",
          awb_track_url: result.awb_track_url ?? "",
          ...(result.declared_value !== undefined && {
            declared_value: result.declared_value,
          }),
        },
        userStore,
      );
      this.setLoadingAndError(this.loading, null);
      return result;
    } catch (error: unknown) {
      this.setLoadingAndError(
        this.loading,
        getShipmentSectionApiErrorMessage(
          error,
          "Failed to update delivery and tracking.",
        ),
      );
      throw error;
    }
  };

  cancelShipment = async (shipmentId: number, reason: string) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.cancelShipment(
        shipmentId,
        reason,
      );
      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          this.shipments[index] = {
            ...this.shipments[index],
            status: 8,
            status_label: "Cancelled",
          };
        }
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  returnShipment = async (shipmentId: number, reason?: string) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.returnShipment(
        shipmentId,
        reason,
      );
      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          this.shipments[index] = {
            ...this.shipments[index],
            status: 7,
            status_label: "Returned",
          };
        }
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  removePacksFromShipment = async (shipmentId: number) => {
    this.setLoadingAndError(true, null);
    try {
      const response =
        await this.shipmentsAPI.removePacksFromShipment(shipmentId);
      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          this.shipments[index] = {
            ...this.shipments[index],
            packs_label: "--",
          };
        }
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  moveShipmentToNew = async (shipmentId: number) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.moveShipmentToNew(shipmentId);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      return undefined;
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  bulkShipmentUpdate = async (
    params: GetShipmentsParams,
    shipment_ids: number[],
    length?: number,
    breadth?: number,
    height?: number,
    weight?: number,
    status?: number,
    delivery_partner_id?: number,
    shipment_method?: ShipmentMethod,
    cancelReason?: string,
    delivery_partner_name?: string,
    booking_type?: ShipmentBookingType,
    declared_value?: number | null,
  ) => {
    this.setLoadingAndError(true, null);
    try {
      const payload: BulkUpdate = {
        shipment_ids,
        ...(length !== undefined && { length }),
        ...(breadth !== undefined && { breadth }),
        ...(height !== undefined && { height }),
        ...(weight !== undefined && { weight }),
        ...(status !== undefined && { status }),
        ...(delivery_partner_id !== undefined && { delivery_partner_id }),
        ...(shipment_method !== undefined && { shipment_method }),
        ...(booking_type !== undefined && { booking_type }),
        ...(cancelReason !== undefined && { cancelReason }),
        ...(declared_value !== undefined && { declared_value }),
      };

      const response = await this.shipmentsAPI.bulkShipmentUpdate(
        payload,
        params,
      );
      if (response.success && this.shipments) {
        shipment_ids.forEach((id) => {
          const index = this.shipments!.findIndex((item) => item.id === id);
          if (index !== -1 && this.shipments) {
            const statusValue =
              status !== undefined
                ? ShipmentStatus.find((s) => s.id === status)?.value
                : undefined;

            const deliveryPartnerName =
              delivery_partner_id !== undefined
                ? delivery_partner_name ||
                  this.deliveryPartners.find(
                    (dp: any) => dp.delivery_partner_id === delivery_partner_id,
                  )?.delivery_partner_name
                : undefined;

            this.shipments[index] = {
              ...this.shipments[index],
              ...(length !== undefined && { length }),
              ...(breadth !== undefined && { breadth }),
              ...(height !== undefined && { height }),
              ...(weight !== undefined && { weight }),
              ...(status !== undefined && {
                status,
                status_label: statusValue,
              }),
              ...(delivery_partner_id !== undefined && {
                delivery_partner_id,
                delivery_partner_name: deliveryPartnerName,
              }),
              ...(shipment_method !== undefined && { shipment_method }),
              ...(booking_type !== undefined && { booking_type }),
              ...(declared_value !== undefined && { declared_value }),
            };
          }
        });
      }

      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  /**
   * Leaves the listing `loading` flag alone: the page swaps the listing (and the
   * upload popup inside it) for a spinner while loading. Throws the server message.
   */
  uploadCsvForBulkEdit = async (
    file: File,
  ): Promise<TrackingBulkUpdateResult | undefined> => {
    const fallbackMessage = "Failed to update tracking details.";
    try {
      const response = await this.shipmentsAPI.uploadCsvForBulkEdit(file);
      if (!response?.success) {
        throw new Error(response?.message || fallbackMessage);
      }
      return response.result;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || error?.message || fallbackMessage,
      );
    }
  };

  /** Polled in the background, so it leaves the listing `loading` flag alone. */
  getTrackingBulkUpdateJob = async (
    jobId: number,
  ): Promise<TrackingBulkUpdateJobStatus> => {
    try {
      const response = await this.shipmentsAPI.getTrackingBulkUpdateJob(jobId);
      if (!response?.success || !response.result) {
        throw new Error(
          response?.message || "Failed to fetch tracking update job.",
        );
      }
      return response.result;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch tracking update job.",
      );
    }
  };

  bulkUpdateShippingCost = async (
    shipmentIds: number[],
    shippingCost: number,
  ): Promise<
    (APIResponse & { result?: BulkUpdateShippingCostResult }) | undefined
  > => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.bulkUpdateShippingCost(
        shipmentIds,
        shippingCost,
      );
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(
        false,
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update shipping cost.",
      );
    }
  };

  syncBooking = async (): Promise<
    (APIResponse & { result?: BulkBookingSyncResult }) | undefined
  > => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.syncBooking();
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, getBookingSyncErrorMessage(error));
    }
  };

  syncShipmentBooking = async (
    shipmentId: number,
    options?: { requeue?: boolean },
  ): Promise<
    (APIResponse & { result?: SingleBookingSyncResult }) | undefined
  > => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.syncShipmentBooking(
        shipmentId,
        options,
      );
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, getBookingSyncErrorMessage(error));
    }
  };

  getOrder = async (orderId: any) => {
    try {
      const response = await this.shipmentsAPI.getOrder(orderId);
      if (response) {
        this.orderIds = (response?.result || []).map((order: any) => ({
          id: String(order.order_id),
          value: String(order.order_id),
        }));
      }
      return response.result;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  downloadShiprocketCSV = async (params: GetShipmentsParams = {}) => {
    try {
      const response = await this.shipmentsAPI.downloadShiprocketCSV(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  getShiprocketCSVBulkReport = async (params: GetShipmentsParams = {}) => {
    try {
      const response =
        await this.shipmentsAPI.getShiprocketCSVBulkReport(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  downloadShipmozoCSV = async (params: GetShipmentsParams = {}) => {
    try {
      const response = await this.shipmentsAPI.downloadShipmozoCSV(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message || error?.message);
      throw error;
    }
  };

  getShipmozoCSVBulkReport = async (params: GetShipmentsParams = {}) => {
    try {
      const response = await this.shipmentsAPI.getShipmozoCSVBulkReport(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  searchPacksByName = async (searchText: string) => {
    try {
      const response = await this.shipmentsAPI.searchPacks(searchText);

      if (response?.result) {
        this.searchedPacks = response.result;
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  getShipmentCreateOptions = async (
    orderId: number,
  ): Promise<ShipmentCreateOptions> => {
    const response = await this.shipmentsAPI.getShipmentCreateOptions(orderId);
    if (!response?.success || !response.result) {
      throw new Error(
        response?.message || "Could not load POD shipment options.",
      );
    }
    return response.result;
  };

  createShipment = async (shipments: ShipmentRequest) => {
    // this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.createShipment(shipments);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  createShipmentThroughCsv = async (orderId: number, file: File | null) => {
    try {
      const response = await this.shipmentsAPI.createShipmentThroughCsv(
        orderId,
        file,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  createBulkShipmentsViaInventoryIdAndCsv = async (
    inventoryId: number,
    file: File | null,
    orderId?: number | null,
  ) => {
    try {
      const response =
        await this.shipmentsAPI.createBulkShipmentsViaInventoryIdAndCsv(
          inventoryId,
          file,
          orderId,
        );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCountByStatus = async () => {
    try {
      const response = await this.shipmentsAPI.getCountByStatus();
      if (response.success) {
        this.countByStatus = response.result;
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getShipmentDetails = async (
    shipmentId: number,
  ): Promise<ShipmentDetailsApiRow> => {
    const response = await this.shipmentsAPI.getShipmentDetails(shipmentId);
    if (!response?.success) {
      throw new Error(response?.message || "Failed to fetch shipment details");
    }
    return response.result;
  };

  addShipmentPackLine = async (
    shipmentId: number,
    payload: AddShipmentPackLineRequest,
  ): Promise<AddShipmentPackLineResult> => {
    try {
      return await this.shipmentsAPI.addShipmentPackLine(shipmentId, payload);
    } catch (error: any) {
      throw error;
    }
  };

  addShipmentItemLine = async (
    shipmentId: number,
    payload: AddShipmentItemLineRequest,
  ): Promise<AddShipmentItemLineResult> => {
    try {
      return await this.shipmentsAPI.addShipmentItemLine(shipmentId, payload);
    } catch (error: any) {
      throw error;
    }
  };

  addShipmentLines = async (
    shipmentIds: number[],
    packs: AddShipmentPackLineRequest[],
    items: AddShipmentItemLineRequest[],
  ): Promise<{ success: boolean; message?: string }> => {
    this.setLoadingAndError(true, null);
    try {
      for (const pack of packs) {
        const payload: BulkAddShipmentPackLineRequest = {
          shipmentIds,
          packId: pack.packId,
          qty: pack.qty,
        };
        await this.shipmentsAPI.bulkAddShipmentPackLine(payload);
      }
      for (const item of items) {
        const payload: BulkAddShipmentItemLineRequest = {
          shipmentIds,
          clientSku: item.clientSku,
          qty: item.qty,
        };
        await this.shipmentsAPI.bulkAddShipmentItemLine(payload);
      }
      this.setLoadingAndError(false, null);
      return { success: true, message: "Lines added successfully" };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add shipment lines";
      this.setLoadingAndError(false, message);
      return { success: false, message };
    }
  };

  /**
   * Validates and queues a pack / item lines CSV. Throws with every failing
   * row in the message when the server rejects the file (nothing written).
   */
  uploadShipmentLinesCsv = async (
    lineType: ShipmentLineType,
    file: File,
  ): Promise<ShipmentLinesUploadResult> => {
    const fallbackMessage = `Failed to upload ${lineType === "PACK" ? "pack" : "item"} lines CSV.`;
    try {
      const response = await this.shipmentsAPI.uploadShipmentLinesCsv(
        lineType,
        file,
      );
      if (!response?.success || !response.result) {
        throw new Error(response?.message || fallbackMessage);
      }
      return response.result;
    } catch (error: unknown) {
      throw new ShipmentLinesUploadError(
        getShipmentLinesUploadErrorMessage(error, fallbackMessage),
        getShipmentLinesUploadRunningJobId(error),
      );
    }
  };

  /**
   * Loads pack / item lines CSV jobs that are still running. Mirrors the backend:
   * a job runs while it has no end time and started under 30 minutes ago.
   */
  fetchRunningLinesUploadJobs = async (): Promise<void> => {
    runInAction(() => {
      this.runningLinesUploadJobsLoading = true;
      this.runningLinesUploadJobsError = null;
    });
    try {
      const responses = await Promise.all(
        [
          JobTypes.BULK_UPLOAD_SHIPMENT_PACK_LINES_BACKGROUND,
          JobTypes.BULK_UPLOAD_SHIPMENT_ITEM_LINES_BACKGROUND,
        ].map((name) =>
          this.jobAPI.getJobs({
            page_no: 1,
            limit: 20,
            name,
            max_progress: 99,
            sort_by: "start_time",
            sort_type: "DESC",
          }),
        ),
      );
      const staleBefore = Date.now() - LINES_UPLOAD_JOB_STALE_MS;
      const running = responses
        .flatMap((response) => response?.list ?? [])
        .filter(
          (job) =>
            !job.end_time &&
            new Date(job.start_time).getTime() > staleBefore,
        )
        .sort(
          (a, b) =>
            new Date(b.start_time).getTime() -
            new Date(a.start_time).getTime(),
        );
      runInAction(() => {
        this.runningLinesUploadJobs = running;
      });
    } catch (error: any) {
      runInAction(() => {
        this.runningLinesUploadJobsError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load running upload jobs.";
      });
    } finally {
      runInAction(() => {
        this.runningLinesUploadJobsLoading = false;
      });
    }
  };

  /** Polled in the background, so it leaves the listing `loading` flag alone. */
  getShipmentLinesUploadJob = async (
    jobId: number,
  ): Promise<ShipmentLinesUploadJobStatus> => {
    try {
      const response = await this.shipmentsAPI.getShipmentLinesUploadJob(jobId);
      if (!response?.success || !response.result) {
        throw new Error(
          response?.message || "Failed to fetch lines upload job.",
        );
      }
      return response.result;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch lines upload job.",
      );
    }
  };

  removeShipmentLine = async (
    shipmentId: number,
    lineId: number,
  ): Promise<RemoveShipmentLineResult> => {
    try {
      return await this.shipmentsAPI.removeShipmentLine(shipmentId, lineId);
    } catch (error: any) {
      throw error;
    }
  };

  removeShipmentPackLines = async (
    shipmentId: number,
    packId: number,
  ): Promise<RemoveShipmentPackLinesResult> => {
    try {
      return await this.shipmentsAPI.removeShipmentPackLines(shipmentId, packId);
    } catch (error: any) {
      throw error;
    }
  };

  addShipmentBox = async (
    shipmentId: number,
    payload: AddShipmentBoxRequest,
  ): Promise<AddShipmentBoxResult> => {
    try {
      return await this.shipmentsAPI.addShipmentBox(shipmentId, payload);
    } catch (error: any) {
      throw error;
    }
  };

  deleteShipmentBox = async (
    shipmentId: number,
    boxId: number,
  ): Promise<DeleteShipmentBoxResult> => {
    try {
      return await this.shipmentsAPI.deleteShipmentBox(shipmentId, boxId);
    } catch (error: any) {
      throw error;
    }
  };

  addShipmentBoxToShipments = async (
    shipmentIds: number[],
    boxes: AddShipmentBoxRequest | AddShipmentBoxRequest[],
  ): Promise<BulkAddShipmentBoxResult> => {
    const payloads = Array.isArray(boxes) ? boxes : [boxes];
    const succeeded: number[] = [];
    const failed: BulkAddShipmentBoxResult["failed"] = [];
    let boxesAdded = 0;

    if (shipmentIds.length === 0 || payloads.length === 0) {
      return { succeeded, failed, boxesAdded };
    }

    for (const shipmentId of shipmentIds) {
      let addedCount = 0;
      let failure: BulkAddShipmentBoxResult["failed"][number] | null = null;

      try {
        for (let boxIndex = 0; boxIndex < payloads.length; boxIndex += 1) {
          try {
            await this.shipmentsAPI.addShipmentBox(
              shipmentId,
              payloads[boxIndex],
            );
            addedCount += 1;
            boxesAdded += 1;
          } catch (error: unknown) {
            failure = {
              shipmentId,
              addedCount,
              requestedCount: payloads.length,
              failedBoxIndex: boxIndex,
              message: getShipmentBoxApiErrorMessage(
                error,
                "Failed to add box",
              ),
            };
            break;
          }
        }
      } catch (error: unknown) {
        failure = {
          shipmentId,
          addedCount,
          requestedCount: payloads.length,
          message: getShipmentBoxApiErrorMessage(
            error,
            "Failed to add boxes",
          ),
        };
      }

      if (failure) {
        failed.push(failure);
      } else {
        succeeded.push(shipmentId);
      }
    }

    return { succeeded, failed, boxesAdded };
  };
}
